import { Immutable } from "immer";
import React, { useContext } from "react";
import { ProductCompareGridSSR } from "tsi-common-react/src/apps/product-compare/ProductCompareGridSSR/index";
import { ProductSelectionGridSSR } from "tsi-common-react/src/apps/product-compare/ProductSelectionGridSSR/index";
import { ProductGridSSR } from "tsi-common-react/src/apps/product-grid2/ProductGridSSR/index";
import { AwardColumnsBlock } from "tsi-common-react/src/components/AwardColumnsBlock/index";
import { BannerBlock } from "tsi-common-react/src/components/BannerBlock/index";
import { ColumnsCarouselBlock } from "tsi-common-react/src/components/ColumnsCarouselBlock/index";
import { ComparisonCarouselBlock } from "tsi-common-react/src/components/ComparisonCarouselBlock/index";
import { CountdownTimer } from "tsi-common-react/src/components/CountdownTimer/index";
import { EmailCaptureContent } from "tsi-common-react/src/components/EmailCaptureContent/index";
import { FAQsBlock } from "tsi-common-react/src/components/FAQsBlock/index";
import { FeaturedOffersCardBlock } from "tsi-common-react/src/components/FeaturedOffersCardBlock/index";
import { FeaturedProductLinesBlock } from "tsi-common-react/src/components/FeaturedProductLinesBlock/index";
import { FeaturedProductsCardBlock } from "tsi-common-react/src/components/FeaturedProductsCardBlock/index";
import { GalleryBlock } from "tsi-common-react/src/components/GalleryBlock/index";
import { HomepageHeroPromoBox } from "tsi-common-react/src/components/HomepageHeroPromoBox/index";
import { PreApprovalBlock } from "tsi-common-react/src/components/PreApprovalBlock/index";
import { ProductCalloutBlock } from "tsi-common-react/src/components/ProductCalloutBlock/index";
import { ProductFeaturesCalloutBlock } from "tsi-common-react/src/components/ProductFeaturesCalloutBlock/index";
import { PromoConditionalBlock } from "tsi-common-react/src/components/PromoConditionalBlock/index";
import { PromotionBlock } from "tsi-common-react/src/components/PromotionBlock/index";
import { TSIAlignedRichTextBlock } from "tsi-common-react/src/components/TSIAlignedRichTextBlock/index";
import { TabCardsBlock } from "tsi-common-react/src/components/TabCardsBlock/index";
import { ValuePropsBarBlock } from "tsi-common-react/src/components/ValuePropsBarBlock/index";
import { getGlobalSpacingClasses } from "tsi-common-react/src/components/_spacing/globalSpacing";
import {
    CountdownTimerTheme,
    type ICountdownTimer,
} from "tsi-common-react/src/models/countdown-timer";
import { isoProductID } from "tsi-common-react/src/models/nominals";
import * as streamfieldModels from "tsi-common-react/src/models/streamfield-blocks";
import { check } from "tsi-common-react/src/models/utils";
import { getReactivatedContext } from "tsi-common-react/src/utils/environment";

import { Context, templates } from "@reactivated";
import type {
    AwardFeatureBlock as AwardFeatureBlockValue,
    BannerBlock as BannerBlockValue,
    ColumnsBlock as ColumnsBlockValue,
    ComparisonCarouselBlock as ComparisonCarouselBlockValue,
    CountdownTimerBlock as CountdownTimerBlockValue,
    EmailCaptureBlock as EmailCaptureBlockValue,
    SharedFAQsBlock as FAQsBlockValue,
    GalleryBlock as GalleryBlockValue,
    HTMLAdBarBlock as HTMLAdBarBlockValue,
    HardcodedBlock as HardcodedBlockValue,
    HotSpotBlock as HotSpotBlockValue,
    InfoBarBlock as InfoBarBlockValue,
    MediaBlock as MediaBlockValue,
    OffersBlock as OffersBlockValue,
    ProductSpecificationsBlock as ProductSpecificationsBlockValue,
    TempurpedicPromoConditionalBlock as PromoConditionalBlockValue,
    PromotionBlock as PromotionBlockValue,
    ReviewsBlock as ReviewsBlockValue,
    SizeGuideBlock as SizeGuideBlockValue,
    SplitKingCalloutBlock as SplitKingCalloutBlockValue,
    TabCardsBlock as TabCardsBlockValue,
    TabFeatureBlock as TabFeatureBlockValue,
    TabsBlock as TabsBlockValue,
    ValuePropBarBlock as ValuePropBarBlockValue,
} from "@reactivated";

import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

// TP-local block components (re-exported as their standalone ``Template``).
import { AccordionSectionBlock } from "../components/AccordionSectionBlock";
import { AnchorBlock } from "../components/AnchorBlock";
import { AwardFeatureBlock } from "../components/AwardFeatureBlock";
import { ColumnsBlock } from "../components/ColumnsBlock";
import { FeaturedProductsTabBlock } from "../components/FeaturedProductsTabBlock";
import { GalleryCarouselBlock } from "../components/GalleryCarouselBlock";
import { HTMLAdBarBlock } from "../components/HTMLAdBarBlock";
import { HardcodedBlock } from "../components/HardcodedBlock";
import { HorizontalRuleBlock } from "../components/HorizontalRuleBlock";
import { HotSpotBlock } from "../components/HotSpotBlock";
import { InfoBarBlock } from "../components/InfoBarBlock";
import { MediaBlock } from "../components/MediaBlock";
import { OffersBlock } from "../components/OffersBlock";
import { ProductSpecificationsBlock } from "../components/ProductSpecificationsBlock";
import { ReviewsBlock } from "../components/ReviewsBlock";
import { SizeGuideBlock } from "../components/SizeGuideBlock";
import { SplitKingCalloutBlock } from "../components/SplitKingCalloutBlock";
import { TabFeatureBlock } from "../components/TabFeatureBlock";
import { Template as BreadcrumbsBlock } from "../templates/BreadcrumbsBlockTemplate";
import { Template as CustomerServiceInfoBlock } from "../templates/CustomerServiceInfoBlockTemplate";
import { TabsBlock } from "./TabsBlock";

/** Same static path the gallery-block SSR context resolves server-side. */
const GALLERY_DEFAULT_THUMBNAIL_PATH = "tsicommon_cms/img/video-thumbnail.jpg";

/** Reads the reactivated ``STATIC_URL`` to rebuild ``{% static %}`` URLs. */
const useStaticURL = (): string => {
    const context = useContext(Context) || getReactivatedContext();
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    return context?.STATIC_URL ?? "/static/";
};

/**
 * `gallery_block` -> shared gallery app component. Rebuilds the three
 * server-computed props the legacy `gallery-block.html` passed
 * (`build_gallery_block_ssr_context`) from the serialized block value (sealy
 * `GalleryBlockEntry` precedent):
 *
 *   - `blockData`: the serialized `GalleryBlock`, minus gallery entries whose
 *     SlideCollection is null (mirrors the SSR helper's filter).
 *   - `defaultThumbnail`: `static('tsicommon_cms/img/video-thumbnail.jpg')`.
 *   - `initialGalleryIndex`: the serialized `initial_gallery_index`, else the
 *     default-gallery index, else 0.
 *
 * Exported with the optional `initialGalleryIndex` override for the PDP
 * assembly (#33589): on a PDP the gallery is the page's MAIN gallery, whose
 * index is computed server-side with the full page-priority chain. When the
 * override is absent (the plain toolbox map entry -- StreamField never passes
 * it), the serialized page-independent index applies.
 */
export const GalleryBlockEntry = (block: {
    value: Immutable<GalleryBlockValue>;
    initialGalleryIndex?: number;
}): React.JSX.Element => {
    const staticURL = useStaticURL();
    const galleries = (block.value.galleries ?? []).filter(
        (g) => g.gallery !== null,
    );
    const defaultIdx = galleries.findIndex(
        (g) => !g.product && !(g.attributes ?? []).length,
    );
    const initialGalleryIndex =
        block.initialGalleryIndex ??
        block.value.initial_gallery_index ??
        (defaultIdx >= 0 ? defaultIdx : 0);
    return (
        <GalleryBlock
            blockData={{ ...block.value, galleries: galleries }}
            defaultThumbnail={`${staticURL}${GALLERY_DEFAULT_THUMBNAIL_PATH}`}
            initialGalleryIndex={initialGalleryIndex}
        />
    );
};

/**
 * Shared Toolbox block dispatcher map for every tempurpedic page-body assembly
 * (#33588). Keys are the EXACT block-name strings from the Python
 * ``ToolboxContent`` / ``ToolboxContainers`` StreamBlocks
 * (``server/cms/assemblies/toolbox*.py``); consumers spread this map and layer
 * their own page-specific overrides on top.
 *
 * Built inside ``buildToolboxComponentsMap`` (``as const``, sealy/cocoon style)
 * so the recursive ``promo_conditional_block`` / ``tabs_block`` entries can
 * reference the full map for their nested ``content`` / ``panel`` StreamBlocks
 * (each itself a ``ToolboxContent``). The map is intentionally NOT annotated
 * with ``StreamFieldBlockMap`` -- the variance is checked by inference at each
 * ``<StreamField components={map}>`` call (which also PROVES, at compile time,
 * that the map covers every ``ToolboxContent`` key, since ``<StreamField>``
 * requires the component map to cover the value union -- the substitute for a
 * dedicated toolbox-serialization test). Mirrors the sealy / stearns / toppers /
 * cocoon ``_toolboxComponentsMap`` precedents.
 *
 * ---------------------------------------------------------------------------
 * PAGE-SUPPLIED SSR SIDE-CHANNEL props (for the #33588 page-body assembly):
 * most entries render from only the serialized ``block.value`` -- correct on
 * the non-PDP toolbox pages (BasicPage / HomePage / UtilityPageWithToolbox /
 * ProductListFilterPage). Several blocks' standalone (#33444-#33473) templates
 * ALSO received page/request-derived props the StreamField cannot supply. The
 * base map renders them with EMPTY defaults; the page-body assembly MUST
 * override these entries keyed by ``block.id`` (sealy ``_productPageBody``
 * side-channel idiom):
 *
 *   - ``hardcoded_block``             -> ``application_source`` (legacy
 *                                        ``page.slug``). Base passes "". The
 *                                        interactive ``hc-block--prequal-card``
 *                                        version NEEDS the real slug threaded.
 *   - ``hotspot_block``               -> ``variant`` (legacy ``hotspot_variant``
 *                                        templatetag). Base passes "".
 *   - ``breadcrumbs_block``           -> ``ancestors`` + ``current_title`` (the
 *                                        block has NO value; ALL data is
 *                                        page-supplied). Base renders an empty
 *                                        breadcrumb -- override with the page's
 *                                        ancestor chain.
 *   - ``customer_service_info_block`` -> StaticBlock (empty value); ALL data
 *                                        (phones, hours, chat-offline-link) comes
 *                                        from the ``customer_service_info_block_
 *                                        data`` templatetag. Base renders empty
 *                                        strings -- override with the settings
 *                                        props.
 *   - ``reviews``                     -> rendered from ``value`` (the block bakes
 *                                        ``value.reviews`` in via
 *                                        ``get_data_for_client_render``), so no
 *                                        side-channel needed on toolbox pages.
 *
 * PDP-only overrides (#33589 PDPModuleAssembly's job, NOT the toolbox
 * page-body): ``gallery_block`` ``initialGalleryIndex`` (GalleryBlockEntry's
 * exported optional override); ``tab_cards_block`` ``initialRootProductID``
 * (base passes 0 -- the configurator-driven PDP supplies the real root product);
 * any product-derived index on the product grids.
 *
 * RENDERS-NULL-FOR-NOW (flagged for reviewers + the page-body chunk): the native
 * serialized toolbox value for ``shop_compare_grid`` and
 * ``tab_feature_attribute_block`` does NOT match the server-FLATTENED envelope
 * their standalone Templates consume (``shop-compare-grid.html`` flattens
 * ``value.tileset`` to a struct whose ``id`` is non-optional;
 * ``tab-feature-attribute-block.html`` runs the block through
 * ``streamfield_client_render_json`` and the configurator container needs the
 * resolved configurator state). Both are product/configurator-context blocks
 * unlikely on plain toolbox pages. The base map renders ``null`` for them; the
 * page-body / #33589 assembly must override them keyed by ``block.id`` with the
 * server-flattened envelope when a page actually surfaces them.
 * ---------------------------------------------------------------------------
 */
const buildToolboxComponentsMap = () => {
    const map = {
        // --- TP-local blocks (dispatch the site-local ``Template``) ---
        accordion_section: AccordionSectionBlock,
        award_feature_block: (block: {
            value: Immutable<AwardFeatureBlockValue>;
        }) => <AwardFeatureBlock value={block.value} />,
        columns_block: (block: { value: Immutable<ColumnsBlockValue> }) => (
            <ColumnsBlock value={block.value} />
        ),
        featured_products_tab_block: FeaturedProductsTabBlock,
        gallery_carousel_block: GalleryCarouselBlock,
        horizontal_rule_block: HorizontalRuleBlock,
        html_ad_bar_block: (block: {
            value: Immutable<HTMLAdBarBlockValue>;
        }) => <HTMLAdBarBlock value={block.value} />,
        info_bar_block: (block: { value: Immutable<InfoBarBlockValue> }) => (
            <InfoBarBlock value={block.value} />
        ),
        media: (block: { value: Immutable<MediaBlockValue> }) => (
            <MediaBlock value={block.value} />
        ),
        offers_block: (block: { value: Immutable<OffersBlockValue> }) => (
            <OffersBlock value={block.value} />
        ),
        product_specifications_block: (block: {
            value: Immutable<ProductSpecificationsBlockValue>;
        }) => <ProductSpecificationsBlock value={block.value} />,
        // `reviews`: side-channel `reviews_url` (the stars-theme rating link) +
        // `brand_color` (the rating's class). Base passes "" -- the consolidated
        // page-body map (`buildToolboxPageMap`) overrides this entry keyed by
        // block.id with the page-derived values, so the full `.reviews-block`
        // wrapper renders on real pages (the breadcrumbs side-channel convention;
        // #33456 wrapper-drop fix).
        reviews: (block: { value: Immutable<ReviewsBlockValue> }) => (
            <ReviewsBlock value={block.value} reviews_url="" brand_color="" />
        ),
        size_guide_block: (block: {
            value: Immutable<SizeGuideBlockValue>;
        }) => <SizeGuideBlock value={block.value} />,
        split_king_callout_block: (block: {
            value: Immutable<SplitKingCalloutBlockValue>;
        }) => <SplitKingCalloutBlock value={block.value} />,
        tab_feature_block: (block: {
            value: Immutable<TabFeatureBlockValue>;
        }) => <TabFeatureBlock value={block.value} />,

        // `hardcoded_block`: side-channel `application_source` (page.slug). Base
        // passes "" -- the page-body assembly overrides keyed by block.id with
        // the real slug (mandatory for the interactive `hc-block--prequal-card`
        // version).
        hardcoded_block: (block: { value: Immutable<HardcodedBlockValue> }) => (
            <HardcodedBlock value={block.value} application_source="" />
        ),

        // `hotspot_block`: side-channel `variant`. Base passes "" -- the
        // page-body assembly overrides keyed by block.id.
        hotspot_block: (block: { value: Immutable<HotSpotBlockValue> }) => (
            <HotSpotBlock value={block.value} variant="" />
        ),

        // `breadcrumbs_block` / `customer_service_info_block`: NO block value;
        // ALL data is page-supplied. Base renders an empty placeholder so the key
        // is covered; the page-body assembly overrides with the page-derived
        // props.
        breadcrumbs_block: () => (
            <BreadcrumbsBlock ancestors={[]} current_title="" />
        ),
        customer_service_info_block: () => (
            <CustomerServiceInfoBlock
                chat_offline_link={null}
                pre_order_phone_rfc3966=""
                pre_order_phone_national=""
                talk_modal_hours_top_html=""
                customer_service_phone_rfc3966=""
                customer_service_phone_national=""
                talk_modal_hours_bottom_html=""
            />
        ),

        // --- Shared blocks (dispatch the shared component with the native
        // serialized value; props mirror each block's standalone
        // <Name>BlockTemplate re-export) ---
        aligned_rich_text_block: TSIAlignedRichTextBlock,
        award_columns_block: AwardColumnsBlock,
        banner_block: (block: { value: Immutable<BannerBlockValue> }) => (
            <BannerBlock value={block.value} />
        ),
        columns_carousel_block: ColumnsCarouselBlock,
        comparison_carousel_block: (block: {
            value: Immutable<ComparisonCarouselBlockValue>;
        }) => <ComparisonCarouselBlock value={block.value} />,
        faqs_block: (block: { value: Immutable<FAQsBlockValue> }) => (
            <FAQsBlock value={block.value} />
        ),
        // GA4 events for the featured-card blocks arrive PRE-BUILT in the
        // serialized value, so plain {value} dispatch is sufficient.
        featured_offers_card_block: FeaturedOffersCardBlock,
        featured_products_card_block: FeaturedProductsCardBlock,
        featured_product_lines_block: FeaturedProductLinesBlock,
        homepage_hero_promo_box: HomepageHeroPromoBox,
        pre_approval_block: PreApprovalBlock,
        product_callout_block: ProductCalloutBlock,
        product_features_callout: ProductFeaturesCalloutBlock,
        // `value_prop_bar_block` -> ValuePropsBarBlock (prop is `valuePropsData`).
        value_prop_bar_block: (block: {
            value: Immutable<ValuePropBarBlockValue>;
        }) => <ValuePropsBarBlock valuePropsData={block.value} />,
        // `promotion_block`: no positional index -- the legacy include_block path
        // rendered every promotion through PromotionBlockTemplate with no index,
        // so the GA4 view_promotion creative_slot was constant (PromotionBlock
        // defaults blockIndex when omitted).
        promotion_block: (block: { value: Immutable<PromotionBlockValue> }) => (
            <PromotionBlock value={block.value} />
        ),

        // `anchor_block` -> AnchorBlock (RegexBlock); value is the raw anchor-id
        // string rendered as `<span id={value}>`.
        anchor_block: (block: { value: Immutable<string> }) => (
            <AnchorBlock value={block.value} />
        ),

        // `countdown_timer` -> reproduces `countdown-timer-block.html`: the
        // legacy `<section class="countdown-timer-block {visibility}">` wrapper
        // around the CountdownTimer island. The native value carries the
        // `timer_queue` snippet's `{is_active, timers}`, so timer rows map
        // directly to `ICountdownTimer[]` (cocoon precedent; no JSON parse). The
        // standalone shim passed NO `timers_expired_promo`, so it stays "".
        countdown_timer: (block: {
            value: Immutable<CountdownTimerBlockValue>;
        }) => {
            const timerData: ICountdownTimer[] = (
                block.value.timer_queue?.timers ?? []
            ).map((timer) => ({
                body: timer.body,
                mobile_body: timer.mobile_body,
                start_date: timer.start_date,
                end_date: timer.end_date,
                theme: timer.theme as CountdownTimerTheme,
            }));
            return (
                <section
                    className={concatClassNames([
                        "countdown-timer-block",
                        block.value.responsive_visibility?.visibility ?? "",
                    ])}
                >
                    <div className="main-container">
                        <div className="countdown-timer-container">
                            <CountdownTimer
                                timerData={timerData}
                                timersExpiredPromo=""
                            />
                        </div>
                    </div>
                </section>
            );
        },

        // `email_capture` -> reproduces email-capture-block.html: maps the
        // block's `email_snippet` into the EmailCaptureContent shape and wraps it
        // in the themed `<section>` (background_style + responsive_visibility --
        // the latter hides the block per breakpoint). Renders nothing when the
        // snippet is absent.
        email_capture: (block: {
            value: Immutable<EmailCaptureBlockValue>;
        }) => {
            const snippet = block.value.email_snippet;
            if (!snippet) {
                return null;
            }
            return (
                <section
                    className={concatClassNames([
                        "email-capture-block",
                        block.value.background_style?.style ?? "",
                        block.value.responsive_visibility?.visibility ?? "",
                    ])}
                >
                    <EmailCaptureContent
                        content={{
                            email_snippet: {
                                title: snippet.title ?? "",
                                content: snippet.content,
                                confirm_content: snippet.confirm_content,
                                image: snippet.image,
                                footnotes: snippet.footnotes,
                            },
                        }}
                    />
                </section>
            );
        },

        // `gallery_block` -> see GalleryBlockEntry docstring.
        gallery_block: GalleryBlockEntry,

        // `tab_cards_block` -> shared TabCardsBlock container. Reproduces the
        // standalone `tab-cards-block.html`: the global-spacing `<section>`
        // wrapper, the io-ts decode the legacy `cms_data` JSON went through, and
        // `initialRootProductID` -- which the server computes from
        // `page.initial_variant*` and is therefore 0 on every (non-PDP) toolbox
        // page. The PDP assembly (#33589) overrides this with the real
        // variant-derived root product id. (sealy precedent.)
        tab_cards_block: (block: { value: Immutable<TabCardsBlockValue> }) => {
            const tabCards = check(
                streamfieldModels.TabCardsBlock.decode(block.value),
            );
            return (
                <section
                    className={getGlobalSpacingClasses(block.value.spacing)}
                >
                    <TabCardsBlock
                        tabCards={tabCards}
                        theme={tabCards.theme.theme || ""}
                        initialRootProductID={isoProductID.wrap(0)}
                    />
                </section>
            );
        },

        // `shop_compare_grid` / `tab_feature_attribute_block`: their NATIVE
        // toolbox value does not match the server-FLATTENED envelope their
        // standalone Templates consume (see header docstring), so the BASE map
        // renders null. Every consolidated Toolbox PAGE body overrides these two
        // keyed by block.id with the server-computed flattened envelope (see
        // `buildToolboxPageMap` in `_toolboxPageMap.tsx`), so they render the
        // real component on real pages -- this base null only applies if a map
        // consumer dispatches them WITHOUT the page-body side-channel.
        shop_compare_grid: () => null,
        tab_feature_attribute_block: () => null,

        // --- SSR-envelope blocks (`block.value` IS the server-computed envelope
        // from get_data_for_client_render) ---

        // `product_grid` -> ProductGridSSR (mirrors ProductGridBlockTemplate).
        product_grid: (block: {
            value: Immutable<templates.ProductGridBlockTemplate> | null;
        }) => (block.value ? <ProductGridSSR {...block.value} /> : null),

        // `product_compare_grid` -> ProductCompareGridSSR. The TP
        // ProductCompareGridBlockTemplate always passes `showFirstAvailablePrice`.
        product_compare_grid: (block: {
            value: Immutable<templates.ProductCompareGridBlockTemplate> | null;
        }) =>
            block.value ? (
                <ProductCompareGridSSR
                    {...block.value}
                    showFirstAvailablePrice
                />
            ) : null,

        // `product_selection_grid` -> ProductSelectionGridSSR (the TP template
        // passes no extra props).
        product_selection_grid: (block: {
            value: Immutable<templates.ProductSelectionGridBlockTemplate> | null;
        }) =>
            block.value ? <ProductSelectionGridSSR {...block.value} /> : null,

        // --- RECURSIVE containers (ToolboxContainers) ---

        // `tabs_block`: TP's TabsBlock dispatches each tab panel's ToolboxContent
        // through this same map (recursive). The render-prop closes over `map` and
        // reproduces the legacy `{% include_block tab.panel %}` per-child
        // `<div class="w-block-{type} block-{type}">` wrappers (Wagtail
        // render_basic; the TP block SCSS targets `.block-{type}` selectors).
        tabs_block: (block: { value: Immutable<TabsBlockValue> }) => (
            <TabsBlock
                value={block.value}
                renderPanel={(panel) =>
                    panel.map((child) => (
                        <StreamField
                            key={child.id}
                            tagName="div"
                            attrs={{
                                className: `w-block-${child.type} block-${child.type}`,
                            }}
                            value={[child]}
                            components={map}
                        />
                    ))
                }
            />
        ),

        // `promo_conditional_block`: TP's subclass
        // (`TempurpedicPromoConditionalBlock` ref) re-types `content` as the TP
        // ToolboxContent, so it dispatches via this same map. Each child is
        // wrapped in `<div class="w-block-{type} block-{type}">` to reproduce the
        // legacy `{% include_block value.content %}` Wagtail render_basic wrappers.
        promo_conditional_block: (block: {
            value: Immutable<PromoConditionalBlockValue>;
        }) => (
            <PromoConditionalBlock value={block.value}>
                {(block.value.content ?? []).map((child) => (
                    <StreamField
                        key={child.id}
                        tagName="div"
                        attrs={{
                            className: `w-block-${child.type} block-${child.type}`,
                        }}
                        value={[child]}
                        components={map}
                    />
                ))}
            </PromoConditionalBlock>
        ),
    } as const;
    return map;
};

export const toolboxComponentsMap = buildToolboxComponentsMap();
